/**
 * Pure helpers for the multi-select sub-product picker drawer
 * (components/product/addSubProducts.js).
 *
 * A product's `subProducts[]` entry is only a *reference* to another product;
 * the backend re-enriches it from the live product on every read and
 * minimizes it on save (backend/lib/subproduct-minimal.js). The whitelist
 * below mirrors that server-side list (minus `md5`/`date`, which
 * Input.addArrayObject stamps on) so local state never carries heavy nested
 * arrays such as the picked product's own `subProducts` or `stockHistory`.
 */

const SUB_PRODUCT_ENTRY_FIELDS = [
    'id',
    'uuidv4',
    'offline',
    'user',
    'customer',
    'externalId',
    'productNumber',
    'type',
    'category',
    'name',
    'description',
    'price',
    'medicine',
    'fee',
    'equipment',
    'other',
    'vat',
];

/**
 * Filter chips shown above the search results. `value` is passed straight to
 * GET /api/products/?category= and must match the product `category` select
 * values defined in lib/fields.js ('' = no filter).
 */
export const SUB_PRODUCT_CATEGORY_FILTERS = [
    { value: '', textId: 'product.subProductPicker.filter-all', text: 'All' },
    { value: 'produkt', textId: 'product.subProductPicker.filter-produkt', text: 'Product' },
    { value: 'honorar', textId: 'product.subProductPicker.filter-honorar', text: 'Fee' },
    { value: 'medisin', textId: 'product.subProductPicker.filter-medisin', text: 'Medicine' },
    { value: 'annet', textId: 'product.subProductPicker.filter-annet', text: 'Other' },
];

function sameId(a, b) {
    if (a === undefined || a === null || b === undefined || b === null) {
        return false;
    }
    return String(a) === String(b);
}

/**
 * @param {Array} subProducts - the product's current subProducts (may be undefined)
 * @param {object} product - a search result
 * @returns {boolean} true when a sub product with the same id already exists
 */
export function isAlreadySubProduct(subProducts, product) {
    if (!Array.isArray(subProducts) || !product || product.id === undefined || product.id === null) {
        return false;
    }
    return subProducts.some(sub => sub && sameId(sub.id, product.id));
}

/**
 * Why a search hit cannot be ticked in the picker.
 * @param {object} object - the product being edited (may lack an id when new)
 * @param {object} product - a search result
 * @returns {'self'|'added'|null}
 */
export function getUnselectableReason(object, product) {
    if (!product || !object) {
        return null;
    }
    if (sameId(object.id, product.id)) {
        return 'self';
    }
    if (isAlreadySubProduct(object.subProducts, product)) {
        return 'added';
    }
    return null;
}

/**
 * Add the product to the selection (qty 1) or remove it when already selected.
 * Always returns a new array.
 */
export function toggleSelection(selected, product) {
    const current = Array.isArray(selected) ? selected : [];
    if (current.some(item => sameId(item.id, product.id))) {
        return current.filter(item => !sameId(item.id, product.id));
    }
    return [...current, { ...product, qty: 1 }];
}

/**
 * Set the (raw, possibly still-being-typed) qty on one selected product.
 * Always returns a new array.
 */
export function setSelectionQty(selected, id, qty) {
    const current = Array.isArray(selected) ? selected : [];
    return current.map(item => (sameId(item.id, id) ? { ...item, qty } : item));
}

/**
 * Remove one product from the selection. Always returns a new array.
 */
export function removeSelection(selected, id) {
    const current = Array.isArray(selected) ? selected : [];
    return current.filter(item => !sameId(item.id, id));
}

/**
 * Build the object handed to Input.addArrayObject (`after`) for one selected
 * product: only the minimal reference fields plus a normalized positive qty.
 */
export function buildSubProductEntry(product) {
    const entry = {};
    SUB_PRODUCT_ENTRY_FIELDS.forEach((key) => {
        if (product[key] !== undefined) {
            entry[key] = product[key];
        }
    });
    const qty = parseFloat(product.qty);
    entry.qty = Number.isFinite(qty) && qty > 0 ? qty : 1;
    return entry;
}
